import apiService from "./api";

export interface Guide {
	id: string;
	name: string;
	spiritualLineId: string;
	personId?: string;
	description?: string;
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
	spiritualLine?: {
		id: string;
		name: string;
		icon?: string;
		primaryColor?: string;
		description?: string;
	};
	person?: {
		id: string;
		firstName: string;
		lastName: string;
		nickname?: string;
	};
	guidePersons?: {
		id: string;
		personId: string;
		guideId: string;
		since: string;
		endedAt?: string;
		isActive: boolean;
		isPrimary: boolean;
		notes?: string;
		person: {
			id: string;
			firstName: string;
			lastName: string;
			nickname?: string;
		};
	}[];
	guidesPerson?: {
		person: {
			id: string;
			name: string;
		};
		assignedAt: string;
		removedAt?: string;
	}[];
}

export interface CreateGuideData {
	name: string;
	spiritualLineId: string;
	personId?: string;
	description?: string;
	isActive?: boolean;
	isPrimary?: boolean;
}

export interface UpdateGuideData {
	name?: string;
	spiritualLineId?: string;
	personId?: string;
	description?: string;
	isActive?: boolean;
	isPrimary?: boolean;
}

export interface FindGuidesParams {
	page?: number;
	limit?: number;
	orderBy?: string;
	order?: "asc" | "desc";
	search?: string;
	name?: string;
	spiritualLineId?: string;
	personId?: string;
	description?: string;
	isActive?: boolean;
}

export interface PaginatedGuides {
	data: Guide[];
	meta: {
		total: number;
		page: number;
		limit: number;
		pages: number;
	};
}

const guideServiceImpl = {
	/**
	 * Obtém todos os guias (sem paginação - para compatibilidade)
	 */
	async getAllGuides(): Promise<Guide[]> {
		const response = await apiService.get("/guides");
		return response.data; // apiService já retorna response.data, então pegamos apenas o array de dados da resposta paginada
	},

	/**
	 * Obtém guias com paginação
	 */
	async getGuides(params?: FindGuidesParams): Promise<PaginatedGuides> {
		const queryParams = new URLSearchParams();

		if (params) {
			Object.entries(params).forEach(([key, value]) => {
				if (value !== undefined && value !== null && value !== "") {
					queryParams.append(key, value.toString());
				}
			});
		}

		const url = `/guides${
			queryParams.toString() ? `?${queryParams.toString()}` : ""
		}`;
		const response = await apiService.get(url);
		return response;
	},

	/**
	 * Obtém um guia específico por ID
	 */
	async getGuideById(id: string): Promise<Guide> {
		const response = await apiService.get(`/guides/${id}`);
		return response;
	},

	/**
	 * Obtém um guia específico por ID com pessoas associadas
	 */
	async findOneWithPersons(id: string): Promise<Guide> {
		const response = await apiService.get(`/guides/${id}/with-persons`);
		return response;
	},

	/**
	 * Cria um novo guia
	 */
	async createGuide(data: CreateGuideData): Promise<Guide> {
		const response = await apiService.post("/guides", data);
		return response;
	},

	/**
	 * Atualiza um guia existente
	 */
	async updateGuide(id: string, data: UpdateGuideData): Promise<Guide> {
		const response = await apiService.patch(`/guides/${id}`, data);
		return response;
	},

	/**
	 * Inativa um guia (soft delete)
	 */
	async deleteGuide(id: string): Promise<void> {
		await apiService.delete(`/guides/${id}`);
	},

	/**
	 * Exclui permanentemente um guia (hard delete).
	 * Só é permitido quando o guia não possui vínculos.
	 */
	async hardDeleteGuide(id: string): Promise<void> {
		await apiService.delete(`/guides/${id}/permanent`);
	},
};

export default guideServiceImpl;
export { guideServiceImpl as guideService };
